import { GpsFilteredData, GpsPoint, HalfEvent, HrData } from "refsix-js-models";

// Helper function to calculate distance between two GPS points using Haversine formula
export const calculateDistance = (point1: GpsPoint, point2: GpsPoint): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (point1.latitude * Math.PI) / 180;
    const φ2 = (point2.latitude * Math.PI) / 180;
    const Δφ = ((point2.latitude - point1.latitude) * Math.PI) / 180;
    const Δλ = ((point2.longitude - point1.longitude) * Math.PI) / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
};

// Helper function to normalize data to 0-1 range
export const normalizeData = (data: number[]): number[] => {
    if (data.length === 0) return [];

    const max = data.reduce((a, b) => Math.max(a, b), -Infinity);
    const min = data.reduce((a, b) => Math.min(a, b), Infinity);
    const range = max - min;

    if (range === 0) return data.map(() => 0.5);

    return data.map(value => (value - min) / range);
};

// Calculate distance traveled per minute from GPS points
export const calculateDistancePerMinute = (gpsData: GpsFilteredData, matchTimings: HalfEvent[]): number[] => {
    if (!gpsData?.geoPoints || gpsData.geoPoints.length === 0) return [];

    const geoPoints = gpsData.geoPoints;
    const startTime = matchTimings[0]?.timestamp || geoPoints[0].time || 0;
    const endTime = matchTimings[matchTimings.length - 1]?.endTime || geoPoints[geoPoints.length - 1].time || 0;
    const durationMinutes = (endTime - startTime) / 60000;

    // Create minute buckets
    const minuteBuckets: number[] = new Array(Math.ceil(durationMinutes)).fill(0);

    // Calculate distance between consecutive points and assign to minute bucket
    for (let i = 1; i < geoPoints.length; i++) {
        const point1 = geoPoints[i - 1];
        const point2 = geoPoints[i];

        const distance = calculateDistance(point1, point2);

        const minuteIndex = Math.floor(((point2.time || 0) - startTime) / 60000);
        if (minuteIndex >= 0 && minuteIndex < minuteBuckets.length) {
            minuteBuckets[minuteIndex] += distance;
        }
    }

    return minuteBuckets;
};

// Calculate peak (maximum) speed per minute from GPS points
export const calculatePeakSpeedPerMinute = (gpsData: GpsFilteredData, matchTimings: HalfEvent[]): number[] => {
    if (!gpsData?.geoPoints || gpsData.geoPoints.length === 0) return [];

    const geoPoints = gpsData.geoPoints;
    const startTime = matchTimings[0]?.timestamp || geoPoints[0].time || 0;
    const endTime = matchTimings[matchTimings.length - 1]?.endTime || geoPoints[geoPoints.length - 1].time || 0;
    const durationMinutes = (endTime - startTime) / 60000;

    // Create minute buckets for peak speeds
    const minuteBuckets: number[] = new Array(Math.ceil(durationMinutes)).fill(0);

    // Track maximum speed in each minute bucket
    for (const point of geoPoints) {
        if (point.speed !== undefined) {
            const minuteIndex = Math.floor(((point.time || 0) - startTime) / 60000);
            if (minuteIndex >= 0 && minuteIndex < minuteBuckets.length) {
                // Keep the maximum speed for this minute
                minuteBuckets[minuteIndex] = Math.max(minuteBuckets[minuteIndex], point.speed);
            }
        }
    }

    return minuteBuckets;
};

// Calculate average heart rate per minute from HR data
export const calculateHeartRatePerMinute = (hrData: HrData, matchTimings: HalfEvent[]): number[] => {
    if (!hrData?.heartRateValues || hrData.heartRateValues.length === 0) return [];

    const hrValues = hrData.heartRateValues;
    const startTime = matchTimings[0]?.timestamp || hrValues[0].time || 0;
    const endTime = matchTimings[matchTimings.length - 1]?.endTime || hrValues[hrValues.length - 1].time || 0;
    const durationMinutes = (endTime - startTime) / 60000;

    // Create minute buckets for heart rates
    const minuteBuckets: { sum: number; count: number }[] = new Array(Math.ceil(durationMinutes))
        .fill(null)
        .map(() => ({ sum: 0, count: 0 }));

    // Assign HR values to minute buckets
    for (const hrPoint of hrValues) {
        if (hrPoint.value !== undefined) {
            const minuteIndex = Math.floor(((hrPoint.time || 0) - startTime) / 60000);
            if (minuteIndex >= 0 && minuteIndex < minuteBuckets.length) {
                minuteBuckets[minuteIndex].sum += hrPoint.value;
                minuteBuckets[minuteIndex].count += 1;
            }
        }
    }

    // Calculate averages
    return minuteBuckets.map(bucket =>
        bucket.count > 0 ? bucket.sum / bucket.count : 0
    );
};

// Calculate percentage of time in HR zones 4+5 (80-100% max HR) per minute
export const calculateHRZone45PercentagePerMinute = (
    hrData: HrData,
    matchTimings: HalfEvent[],
    maxHR: number
): number[] => {
    if (!hrData?.heartRateValues || hrData.heartRateValues.length === 0) return [];

    const hrValues = hrData.heartRateValues;
    const startTime = matchTimings[0]?.timestamp || hrValues[0].time || 0;
    const endTime = matchTimings[matchTimings.length - 1]?.endTime || hrValues[hrValues.length - 1].time || 0;
    const durationMinutes = (endTime - startTime) / 60000;

    // Create minute buckets tracking time in zones 4+5
    const minuteBuckets: { totalTime: number; zone45Time: number }[] = new Array(Math.ceil(durationMinutes))
        .fill(null)
        .map(() => ({ totalTime: 0, zone45Time: 0 }));

    // Process each HR point
    for (let i = 0; i < hrValues.length; i++) {
        const hrPoint = hrValues[i];
        if (hrPoint.value !== undefined && hrPoint.time !== undefined) {
            const minuteIndex = Math.floor(((hrPoint.time || 0) - startTime) / 60000);

            if (minuteIndex >= 0 && minuteIndex < minuteBuckets.length) {
                // Calculate time duration for this reading (time until next reading)
                const duration = i < hrValues.length - 1
                    ? (hrValues[i + 1].time || 0) - hrPoint.time
                    : 1000; // Default 1 second for last point

                // Determine if this HR value is in zone 4 or 5 (80-100% max HR)
                const percentageOfMax = hrPoint.value / maxHR;
                const isZone45 = percentageOfMax >= 0.8 && percentageOfMax <= 1.0;

                minuteBuckets[minuteIndex].totalTime += duration;
                if (isZone45) {
                    minuteBuckets[minuteIndex].zone45Time += duration;
                }
            }
        }
    }

    // Calculate percentage for each minute
    return minuteBuckets.map(bucket =>
        bucket.totalTime > 0 ? (bucket.zone45Time / bucket.totalTime) : 0
    );
};

// Calculate acceleration between consecutive GPS points
export const calculateAcceleration = (point1: GpsPoint, point2: GpsPoint): number => {
    if (!point1.speed || !point2.speed || !point1.time || !point2.time) {
        return 0;
    }
    const timeDiffSeconds = (point2.time - point1.time) / 1000;
    if (timeDiffSeconds <= 0) return 0;

    return (point2.speed - point1.speed) / timeDiffSeconds; // m/s²
};

// Calculate metabolic power (simplified - without body mass)
export const calculateMetabolicPower = (speed: number, acceleration: number): number => {
    const equivalentSlope = Math.atan(Math.abs(acceleration) / 9.81);
    return speed * (3.6 + equivalentSlope * 9.81);
};

// Calculate standard deviation of an array
export const calculateStdDev = (values: number[]): number => {
    if (values.length === 0) return 0;
    const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
    const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
    return Math.sqrt(variance);
};

// Clamp value between min and max
export const clamp = (value: number, min: number, max: number): number => {
    return Math.max(min, Math.min(max, value));
};

// Apply sigmoid transformation for sharper transitions
export const sharpen = (value: number, steepness: number = 10, midpoint: number = 0.4): number => {
    return 1 / (1 + Math.exp(-steepness * (value - midpoint)));
};

// Calculate composite intensity per minute from GPS and HR data
export const calculateCompositeIntensity = (
    gpsData: GpsFilteredData,
    hrData: HrData | undefined,
    matchTimings: HalfEvent[],
    maxHR: number
): number[] => {
    if (!gpsData?.geoPoints || gpsData.geoPoints.length === 0) return [];

    const geoPoints = gpsData.geoPoints;
    const startTime = matchTimings[0]?.timestamp || geoPoints[0].time || 0;
    const endTime = matchTimings[matchTimings.length - 1]?.endTime || geoPoints[geoPoints.length - 1].time || 0;
    const durationMinutes = (endTime - startTime) / 60000;

    // Create minute buckets for each metric
    interface MetricBucket {
        totalDistance: number;
        highIntensityDistance: number;
        speeds: number[];
        metabolicPowers: number[];
        hrValues: number[];
    }

    const minuteBuckets: MetricBucket[] = new Array(Math.ceil(durationMinutes))
        .fill(null)
        .map(() => ({
            totalDistance: 0,
            highIntensityDistance: 0,
            speeds: [],
            metabolicPowers: [],
            hrValues: []
        }));

    // Process GPS data
    for (let i = 1; i < geoPoints.length; i++) {
        const point1 = geoPoints[i - 1];
        const point2 = geoPoints[i];

        const minuteIndex = Math.floor(((point2.time || 0) - startTime) / 60000);
        if (minuteIndex >= 0 && minuteIndex < minuteBuckets.length) {
            // Distance
            const distance = calculateDistance(point1, point2);
            minuteBuckets[minuteIndex].totalDistance += distance;

            // High-intensity distance (speed >= 5.47 m/s = fast run threshold)
            if (point2.speed && point2.speed >= 5.47) {
                minuteBuckets[minuteIndex].highIntensityDistance += distance;
            }

            // Speed variability
            if (point2.speed !== undefined) {
                minuteBuckets[minuteIndex].speeds.push(point2.speed);
            }

            // Metabolic power
            if (point1.speed !== undefined && point2.speed !== undefined) {
                const acceleration = calculateAcceleration(point1, point2);
                const power = calculateMetabolicPower(point2.speed, acceleration);
                minuteBuckets[minuteIndex].metabolicPowers.push(power);
            }
        }
    }

    // Process HR data
    if (hrData?.heartRateValues) {
        for (const hrPoint of hrData.heartRateValues) {
            if (hrPoint.value !== undefined && hrPoint.time !== undefined) {
                const minuteIndex = Math.floor(((hrPoint.time || 0) - startTime) / 60000);
                if (minuteIndex >= 0 && minuteIndex < minuteBuckets.length) {
                    minuteBuckets[minuteIndex].hrValues.push(hrPoint.value);
                }
            }
        }
    }

    // Calculate composite intensity for each minute
    const intensityScores: number[] = [];

    for (let i = 0; i < minuteBuckets.length; i++) {
        const bucket = minuteBuckets[i];

        // 1. Distance per minute (normalize to 0-1, 150m/min = high)
        const distanceNorm = clamp(bucket.totalDistance / 150, 0, 1);

        // 2. Metabolic power (average, normalize to 0-1, 25 W/kg = very high)
        const avgPower = bucket.metabolicPowers.length > 0
            ? bucket.metabolicPowers.reduce((sum, p) => sum + p, 0) / bucket.metabolicPowers.length
            : 0;
        const powerNorm = clamp(avgPower / 25, 0, 1);

        // 3. High-intensity distance percentage
        const hiDistPct = bucket.totalDistance > 0
            ? bucket.highIntensityDistance / bucket.totalDistance
            : 0;

        // 4. Speed variability (std dev, normalize to 0-1, 3 m/s std dev = high)
        const speedVariability = calculateStdDev(bucket.speeds);
        const variabilityNorm = clamp(speedVariability / 3, 0, 1);

        // 5. HR zone (normalize to 0-1 based on max HR)
        const avgHR = bucket.hrValues.length > 0
            ? bucket.hrValues.reduce((sum, hr) => sum + hr, 0) / bucket.hrValues.length
            : 0;
        const hrZoneNorm = avgHR > 0 ? clamp(avgHR / maxHR, 0, 1) : 0;

        // 6. HR trend (rate of change from previous minute)
        let hrTrendNorm = 0.5; // Default neutral
        if (i > 0 && bucket.hrValues.length > 0 && minuteBuckets[i - 1].hrValues.length > 0) {
            const prevAvgHR = minuteBuckets[i - 1].hrValues.reduce((sum, hr) => sum + hr, 0) / minuteBuckets[i - 1].hrValues.length;
            const hrChange = (avgHR - prevAvgHR) / maxHR; // Normalize change
            hrTrendNorm = clamp((hrChange + 1) / 2, 0, 1); // -1 to 1 → 0 to 1
        }

        // Weighted combination (GPS metrics dominate)
        const intensity = (
            0.30 * distanceNorm +
            0.25 * powerNorm +
            0.15 * hiDistPct +
            0.10 * variabilityNorm +
            0.15 * hrZoneNorm +
            0.05 * hrTrendNorm
        );

        // Apply sigmoid sharpening for clearer transitions
        const sharpenedIntensity = sharpen(intensity, 10, 0.4);

        intensityScores.push(sharpenedIntensity);
    }

    return intensityScores;
};
